import { gql, type MutationOptions } from "@apollo/client";

import { SAMPLE_LOCKING_ACTIONS } from "@/settings/settings.ts";

import { stringifyLikeJson } from "../app-utils/graphql.js";
import {
  type InputAddProjectMemberInput,
  type InputAdvancedSearchInput,
  type InputAttributeInput,
  type InputColumnInput,
  type InputCoordinateListInput,
  type InputCreateGroupInput,
  type InputDashboardInput,
  type InputEditParentInput,
  type InputEditSamplesInput,
  type InputGenomeAssembliesInput,
  type InputJobInput,
  type InputModifyOrganismInput,
  type InputModifyPlaybookEventInput,
  type InputModifyProjectFileInput,
  type InputModifyProjectInput,
  type InputModifySavedItemInput,
  type InputNfParamInput,
  type InputOrganismPropertyTypeInput,
  type InputParentInput,
  type InputParentTypeInput,
  type InputPlaybookCategoryInput,
  type InputPlaybookEventInput,
  type InputPlaybookInput,
  type InputRemoveProjectMemberInput,
  type InputRoleInput,
  type InputSampleTypeInput,
  type InputSearchEntityTypeInput,
  type InputSequenceUploadInput,
  type InputSetComparisonGroupInput,
  type InputTableQuerySpec,
  type InputUpdateProjectMemberRoleInput,
  type InputUserAccountInput,
  type InputUserGroupInput,
  type InputUserParamsInput,
  type InputUserPasswordChangeRequestInput,
  type InputViewInput,
  type InputWorkflowParamInput,
  type MutationGroupByTypeArgs,
  type MutationGroupInLegacyPortalArgs,
} from "../graphql.js";
import { Fragments } from "./fragments.js";
import { type ICreateProjectParams, type IInputTableQuerySpecWithContext } from "./WebApiClient.js";

export interface ICustomMutationOptions extends MutationOptions {
  rootField: string; // needed to retrieve the correct set of data out of the returned response
}

export function getSubmitJobMutationOptions(jobInput: InputJobInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation submitJob($jobInputData: InputJobInput) {
        submitJob(jobInputData: $jobInputData) {
          id
          name
          status
        }
      }
    `,
    variables: {
      jobInputData: jobInput,
    },
    rootField: "submitJob",
  };
}

export function getSubmitSequencesUploadMutationOptions(
  sequenceUploadInput: InputSequenceUploadInput,
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation submitSequencesUpload($sequenceUploadInput: InputSequenceUploadInput) {
        submitSequencesUpload(sequenceUploadInput: $sequenceUploadInput)
      }
    `,
    variables: {
      sequenceUploadInput: sequenceUploadInput,
    },
    rootField: "submitSequencesUpload",
  };
}

export function getSubmitCoordinatesListUploadMutationOptions(
  coordListUploadInput: InputCoordinateListInput,
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation submitCoordinatesListUpload($coordListUploadInput: InputCoordinateListInput) {
        createCoordinateList(coordinateListInputData: $coordListUploadInput) {
          id
        }
      }
    `,
    variables: {
      coordListUploadInput: coordListUploadInput,
    },
    rootField: "createCoordinatesList",
  };
}

export function getUpdateUserParamsMutationOptions(userParams: InputUserParamsInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation updateUserParams($userParams: InputUserParamsInput) {
        updateUserParams(userParams: $userParams) {
          ...UserParams
        }
      }
      ${Fragments.userParams}
    `,
    variables: {
      userParams: userParams,
    },
    rootField: "updateUserParams",
  };
}

export function getCreateSearchResultMutationOptions(
  searchTerm: string,
  types: InputSearchEntityTypeInput[],
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createSearchResult($searchTerm: String!, $types: [InputSearchEntityTypeInput]!) {
        search {
          createSearchResult(searchTerm: $searchTerm, types: $types) {
            ...SearchResult
          }
        }
      }
      ${Fragments.searchResult}
    `,
    variables: {
      searchTerm: searchTerm,
      types: types,
    },
    rootField: "search.createSearchResult",
  };
}

export function getCreateAdvancedSearchResultMutationOptions(
  advancedSearchInput: InputAdvancedSearchInput,
): ICustomMutationOptions {
  const stringifiedAdvancedSearchInput: string = stringifyLikeJson(advancedSearchInput);

  return {
    mutation: gql`
      mutation createAdvancedSearchResult {
        search {
          createAdvancedSearchResult(searchParams: ${stringifiedAdvancedSearchInput}) {
            ...SearchResult
          }
        }
      }
      ${Fragments.searchResult}
    `,
    variables: {},
    rootField: "search.createAdvancedSearchResult",
  };
}

export function getDeleteGroupsQueryOptions(ids: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteGroups($groupIds: [Long]!) {
        deleteGroups(groupIds: $groupIds) {
          id
        }
      }
    `,
    variables: {
      groupIds: ids,
    },
    rootField: "deleteGroups",
  };
}

export function getDeleteViewsQueryOptions(ids: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteViews($viewIds: [Long]!) {
        deleteViews(viewIds: $viewIds) {
          id
        }
      }
    `,
    variables: {
      viewIds: ids,
    },
    rootField: "deleteViews",
  };
}

// TODO: remove (Used in Saved Items)
export function getDeleteFlagsQueryOptions(ids: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteRegionFlags($regionFlagIds: [Long]!) {
        deleteRegionFlags(regionFlagIds: $regionFlagIds) {
          ...RegionFlag
        }
      }
      ${Fragments.regionFlag}
    `,
    variables: {
      regionFlagIds: ids,
    },
    rootField: "deleteRegionFlags",
  };
}

export function getDeleteCoordinateListsQueryOptions(ids: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteCoordinateLists($coordinateListIds: [Long!]!) {
        deleteCoordinatesList(coordinateListsIds: $coordinateListIds)
      }
    `,
    variables: {
      coordinateListIds: ids,
    },
    rootField: "deleteCoordinateLists",
  };
}

export function getCreateProjectHyperlink(
  name: string,
  url: string,
  folderId: number,
  description?: string,
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createProjectHyperlink($name: String!, $description: String, $url: String, $folderId: Long!) {
        createProjectHyperlink(name: $name, description: $description, url: $url, folderId: $folderId) {
          ...ProjectFile
        }
      }
      ${Fragments.projectFile}
    `,
    variables: {
      name,
      description,
      url,
      folderId,
    },
    rootField: "createProjectHyperlink",
  };
}

export function getCreateProjectFolderQueryOptions(name: string, folderId: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createProjectFolder($name: String!, $folderId: Long!) {
        createProjectFolder(name: $name, folderId: $folderId)
      }
    `,
    variables: {
      name,
      folderId,
    },
    rootField: "createProjectFolder",
  };
}

export function getDeleteActivitiesQueryOptions(ids: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteActivities($activityIds: [Long]!) {
        deleteActivities(activityIds: $activityIds)
      }
    `,
    variables: {
      activityIds: ids,
    },
    rootField: "deleteActivities",
  };
}

export function getDeleteProjectQueryOptions(projectId: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteProject($projectId: Long!) {
        deleteProject(projectId: $projectId)
      }
    `,
    variables: {
      projectId: projectId,
    },
    rootField: "deleteProject",
  };
}

export function getCreateProjectCommentQueryOptions(
  projectId: number,
  comment: string,
  groupIds: number[],
  viewIds: number[],
  flagIds: number[],
  projectFileIds: number[],
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createProjectComment(
        $projectId: Long!
        $comment: String!
        $groupIds: [Long]
        $viewIds: [Long]
        $flagIds: [Long]
        $reportIds: [Long]
      ) {
        createProjectComment(
          projectId: $projectId
          comment: $comment
          groupIds: $groupIds
          viewIds: $viewIds
          flagIds: $flagIds
          reportIds: $reportIds
        ) {
          ...ProjectActivity
        }
      }
      ${Fragments.projectActivity}
    `,
    variables: {
      projectId: projectId,
      comment: comment,
      groupIds: groupIds,
      viewIds: viewIds,
      flagIds: flagIds,
      reportIds: projectFileIds,
    },
    rootField: "createProjectComment",
  };
}

export function getCreateProjectMutationOptions(params: ICreateProjectParams): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createProject(
        $name: String!
        $isPublic: Boolean!
        $description: String
        $emailNotification: Boolean
        $userNames: [String]!
        $userGroupId: Long!
        $capabilityIds: [Long]!
      ) {
        createProject(
          projectInputData: {
            name: $name
            isPublic: $isPublic
            description: $description
            userNames: $userNames
            emailNotification: $emailNotification
            userGroupId: $userGroupId
            capabilityIds: $capabilityIds
          }
        ) {
          ...ProjectBasics
        }
      }
      ${Fragments.projectBasics}
    `,
    variables: {
      name: params.name,
      isPublic: params.isPublic,
      description: params.description,
      userNames: params.userNames,
      emailNotification: params.emailNotification,
      userGroupId: params.userGroupId,
      capabilityIds: params.capabilityIds,
    },
    rootField: "createProject",
  };
}

export function getCreateUsergroupMutationOptions(params: InputUserGroupInput): ICustomMutationOptions {
  const stringifiedParams: string = stringifyLikeJson(params);
  return {
    mutation: gql`
      mutation userGroupQuery {
        userGroup {
          create( userGroupInputData: ${stringifiedParams})
          {
            ...UserGroup
          }
        }
      }
      ${Fragments.userGroup}
    `,
    variables: {},
    rootField: "userGroup.create",
  };
}

export function getModifyProjectMutationOptions(params: InputModifyProjectInput): ICustomMutationOptions {
  const inputData: string = stringifyLikeJson(params);
  return {
    mutation: gql`
      mutation modifyProject {
        modifyProject( modifyProjectInput: ${inputData})
        {
          ...ProjectBasics
        }
      }
      ${Fragments.projectBasics}
    `,
    variables: {},
    rootField: "modifyProject",
  };
}

export function getCreateGroupMutationOptions(params: InputCreateGroupInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createGroup($createGroupInput: InputCreateGroupInput!) {
        createGroup(createGroupInput: $createGroupInput) {
          ...GroupBasics
        }
      }
      ${Fragments.groupBasics}
    `,
    variables: {
      createGroupInput: params,
    },
    rootField: "createGroup",
  };
}

export function getCreateViewMutationOptions(params: InputViewInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation saveView($viewInputData: InputViewInput) {
        saveView(viewInputData: $viewInputData) {
          id
          name
        }
      }
    `,
    variables: {
      viewInputData: params,
    },
    rootField: "saveView",
  };
}

export function getUpdateProjectRoleMutation(input: InputUpdateProjectMemberRoleInput): ICustomMutationOptions {
  const inputJSON: string = stringifyLikeJson(input);
  return {
    mutation: gql`
      mutation updateProjectRole {
        administrateProject  {
          updateProjectRole(updateProjectMemberRoleInputData: ${inputJSON})
        }
      }
    `,
    variables: {},
    rootField: "administrateProject.updateProjectRole",
  };
}

export function getAddProjectMemberMutation(input: InputAddProjectMemberInput): ICustomMutationOptions {
  const inputJSON: string = stringifyLikeJson(input);
  return {
    mutation: gql`
      mutation addUsersToProject {
        administrateProject  {
          addUsers(addProjectMemberInputData: ${inputJSON})
          {
            ...ProjectMember
          }
        }
      }
      ${Fragments.projectMember}
    `,
    variables: {},
    rootField: "administrateProject.addUsers",
  };
}

export function getRemoveProjectMemberMutation(input: InputRemoveProjectMemberInput): ICustomMutationOptions {
  const inputJSON: string = stringifyLikeJson(input);
  return {
    mutation: gql`
      mutation removeUserFromProject {
        administrateProject {
          removeUser(removeProjectMemberInputData: ${inputJSON})
        }
      }
    `,
    variables: {},
    rootField: "administrateProject.removeUser",
  };
}

export function setNotificationAsRead(notificationId: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation markNotificationAsRead($notificationId: Long!) {
        markNotificationAsRead(notificationId: $notificationId)
      }
    `,
    variables: {
      notificationId: notificationId,
    },
    rootField: "markNotificationAsRead",
  };
}

export function setAllNotificationsAsRead(): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation markAllNotificationsAsRead {
        markAllNotificationsAsRead
      }
    `,
    variables: {},
    rootField: "markAllNotificationsAsRead",
  };
}

export function getCopyViews(viewIds: number[], projectId: number, isPrivate: boolean): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation copyViews($viewIds: [Long]!, $projectId: Long!, $isPrivate: Boolean!) {
        copyViews(viewIds: $viewIds, projectId: $projectId, isPrivate: $isPrivate)
      }
    `,
    variables: {
      viewIds: viewIds,
      projectId: projectId,
      isPrivate: isPrivate,
    },
    rootField: "copyViews",
  };
}

export function getCopyFlags(flagIds: number[], projectId: number, isPrivate: boolean): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation copyRegionFlags($regionFlagIds: [Long]!, $projectId: Long!, $isPrivate: Boolean!) {
        copyRegionFlags(regionFlagIds: $regionFlagIds, projectId: $projectId, isPrivate: $isPrivate)
      }
    `,
    variables: {
      regionFlagIds: flagIds,
      projectId: projectId,
      isPrivate: isPrivate,
    },
    rootField: "copyRegionFlags",
  };
}

export function getCopyFilesOptions(fileIds: number[], targetId: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation copyProjectFiles($fileIds: [Long]!, $targetId: Long!) {
        copyProjectFiles(fileIds: $fileIds, targetId: $targetId)
      }
    `,
    variables: {
      fileIds: fileIds,
      targetId: targetId,
    },
    rootField: "copyProjectFiles",
  };
}

export function getMoveFilesOptions(fileIds: number[], targetId: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation moveProjectFiles($fileIds: [Long]!, $targetId: Long!) {
        moveProjectFiles(fileIds: $fileIds, targetId: $targetId)
      }
    `,
    variables: {
      fileIds: fileIds,
      targetId: targetId,
    },
    rootField: "moveProjectFiles",
  };
}

export function getCreateGroupFromSetComparison(payload: InputSetComparisonGroupInput): ICustomMutationOptions {
  const inputJSON: string = stringifyLikeJson(payload);
  return {
    mutation: gql`
      mutation createGroupFromSetComparison {
        createGroupFromSets(
          setComparisonGroupInput: ${inputJSON}
        )
      }
    `,
    variables: {},
    rootField: "createGroupFromSets",
  };
}

export function getCopyGroups(groupIds: number[], projectId: number, isPrivate: boolean): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation copyGroups($groupIds: [Long]!, $projectId: Long!, $isPrivate: Boolean!) {
        copyGroups(groupIds: $groupIds, projectId: $projectId, isPrivate: $isPrivate)
      }
    `,
    variables: {
      groupIds: groupIds,
      projectId: projectId,
      isPrivate: isPrivate,
    },
    rootField: "copyGroups",
  };
}

export function getGroupRelated(payload: MutationGroupByTypeArgs): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation groupRelated($targetType: String!, $tableQuerySpec: InputTableQuerySpec!, $groupName: String!) {
        groupByType(targetType: $targetType, tableQuerySpec: $tableQuerySpec, groupName: $groupName) {
          ...GroupBasics
        }
      }
      ${Fragments.groupBasics}
    `,
    variables: payload,
    rootField: "groupByType",
  };
}

export function getCreateGroupInClassicPortal(input: MutationGroupInLegacyPortalArgs): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation groupInLegacyPortal($tableQuerySpec: InputTableQuerySpec!, $groupName: String!) {
        groupInLegacyPortal(tableQuerySpec: $tableQuerySpec, groupName: $groupName) {
          id
          typeId
        }
      }
    `,
    variables: {
      tableQuerySpec: input.tableQuerySpec,
      groupName: input.groupName,
    },
    rootField: "groupInLegacyPortal",
  };
}

export function getLogoutMutationOptions(): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation logout {
        logout
      }
    `,
    variables: {},
    rootField: "logout",
  };
}

export function getUpdateSavedItemOptions(payload: InputModifySavedItemInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifySavedItem($modifySavedItemInput: InputModifySavedItemInput) {
        modifySavedItem(modifySavedItemInput: $modifySavedItemInput) {
          ...VariadicSavedItem
        }
      }
      ${Fragments.variadicSavedItem}
    `,
    variables: {
      modifySavedItemInput: payload,
    },
    rootField: "modifySavedItem",
  };
}

export function getModifyUserGroupQueryOptions(userGroupInput: InputUserGroupInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifyUserGroup($userGroupInputData: InputUserGroupInput) {
        userGroup {
          modify(userGroupInputData: $userGroupInputData) {
            ...UserGroupBasics
          }
        }
      }
      ${Fragments.userGroupBasics}
    `,
    variables: {
      userGroupInputData: userGroupInput,
    },
    rootField: "userGroup.modify",
  };
}

export function getDeleteUserGroupQueryOptions(id: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteUserGroup($userGroupId: Long!) {
        userGroup {
          delete(userGroupId: $userGroupId)
        }
      }
    `,
    variables: {
      userGroupId: id,
    },
    rootField: "userGroup.delete",
  };
}

export function getCreateGroupsUnionQueryOptions(groupIds: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createGroupsUnion($groupIds: [Long]!) {
        createUnionGroup(groupIds: $groupIds) {
          id
        }
      }
    `,
    variables: {
      groupIds: groupIds,
    },
    rootField: "createUnionGroup.id",
  };
}

export function getCreateSampleGroupFromParentsUnionQueryOptions(parentIds: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createUnionSampleGroupFromParents($parentIds: [Long]!) {
        sample {
          createUnionSampleGroupFromParents(parentIds: $parentIds) {
            id
          }
        }
      }
    `,
    variables: {
      parentIds: parentIds,
    },
    rootField: "sample.createUnionSampleGroupFromParents.id",
  };
}

export function getCreateTempGroupQueryOptions(selectionSpec: InputTableQuerySpec): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createTempGroup($tableQuerySpec: InputTableQuerySpec!) {
        createTemporaryGroup(tableQuerySpec: $tableQuerySpec) {
          id
        }
      }
    `,
    variables: {
      tableQuerySpec: selectionSpec,
    },
    rootField: "createTemporaryGroup.id",
  };
}

export function getRunPlaybookQueryOptions(
  registrationInput: InputParentInput,
  playbookEventInput: InputPlaybookEventInput,
  workflowParamInputValues: InputWorkflowParamInput[],
  editingInput: InputEditSamplesInput,
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation runPlaybook(
        $parentInputData: InputParentInput
        $playbookEventInput: InputPlaybookEventInput
        $workflowParams: [InputWorkflowParamInput]
        $editSamplesInput: InputEditSamplesInput
      ) {
        playbook {
          runPlaybook(
            parentInputData: $parentInputData
            playbookEventInput: $playbookEventInput
            workflowParams: $workflowParams
            editSamplesInput: $editSamplesInput
          ) {
            id
            name
            parentType {
              id
              name
            }
          }
        }
      }
    `,
    variables: {
      parentInputData: registrationInput,
      playbookEventInput: playbookEventInput,
      workflowParams: workflowParamInputValues,
      editSamplesInput: editingInput,
    },
    rootField: "playbook.runPlaybook",
  };
}

export function getSaveEditedSampleQueryOptions(editSamplesInput: InputEditSamplesInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation editSamples($editSamplesInput: InputEditSamplesInput) {
        sample {
          editSamples(editSamplesInput: $editSamplesInput)
        }
      }
    `,
    variables: {
      editSamplesInput: editSamplesInput,
    },
    rootField: "sample.editSamples",
  };
}

export function getUpdateActiveTemplateQueryOptions(
  orderedColumns: InputColumnInput[],
  tableSpec: InputTableQuerySpec,
): ICustomMutationOptions {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { context, ...cleanedUpTableQuerySpec } = tableSpec as IInputTableQuerySpecWithContext; // remove the context if set

  return {
    mutation: gql`
      mutation updateActiveTemplate($tableSpec: InputTableQuerySpec, $columns: [InputColumnInput]) {
        template {
          updateActive(tableSpec: $tableSpec, columns: $columns)
        }
      }
    `,
    variables: {
      tableSpec: cleanedUpTableQuerySpec,
      columns: orderedColumns,
    },
    rootField: "template.update",
  };
}

export function getSaveColumnTemplateQueryOptions(
  name: string,
  tableSpec: InputTableQuerySpec,
  columns: InputColumnInput[],
): ICustomMutationOptions {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { context, ...cleanedUpTableQuerySpec } = tableSpec as IInputTableQuerySpecWithContext; // remove the context if set

  return {
    mutation: gql`
      mutation saveColumnTemplate($name: String, $tableSpec: InputTableQuerySpec!, $columns: [InputColumnInput]) {
        template {
          saveTemplate(name: $name, tableSpec: $tableSpec, columns: $columns) {
            ...Template
          }
        }
      }
      ${Fragments.columnTemplate}
    `,
    variables: {
      name: name,
      tableSpec: cleanedUpTableQuerySpec,
      columns: columns,
    },
    rootField: "template.saveTemplate",
  };
}

export function getUpdateColumnTemplateQueryOptions(
  templateId: number,
  columns: InputColumnInput[],
  tableSpec: InputTableQuerySpec,
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation updateColumnTemplate($templateId: Long, $columns: [InputColumnInput], $tableSpec: InputTableQuerySpec!) {
        template {
          updateTemplate(templateId: $templateId, columns: $columns, tableSpec: $tableSpec) {
            ...Template
          }
        }
      }
      ${Fragments.columnTemplate}
    `,
    variables: {
      templateId: templateId,
      columns: columns,
      tableSpec: tableSpec,
    },
    rootField: "template.updateTemplate",
  };
}

export function getDeleteColumnTemplateQueryOptions(templateId: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteTemplate($templateId: Long) {
        template {
          deleteTemplate(templateId: $templateId)
        }
      }
    `,
    variables: {
      templateId: templateId,
    },
    rootField: "template.deleteTemplate",
  };
}

export function getUpdateRoleQueryOptions(payload: InputRoleInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation updateRole($roleInput: InputRoleInput) {
        admin {
          updateRole(roleInput: $roleInput) {
            ...RoleAdmin
          }
        }
      }
      ${Fragments.roleAdmin}
    `,
    variables: {
      roleInput: payload,
    },
    rootField: "admin.updateRole",
  };
}

export function getCreateRoleQueryOptions(payload: InputRoleInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createRole($roleInput: InputRoleInput) {
        admin {
          createRole(roleInput: $roleInput) {
            ...RoleAdmin
          }
        }
      }
      ${Fragments.roleAdmin}
    `,
    variables: {
      roleInput: payload,
    },
    rootField: "admin.createRole",
  };
}

export function getCreateAttributeQueryOptions(payload: InputAttributeInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createAttribute($createAttributeInput: InputAttributeInput) {
        sample {
          createAttribute(createAttributeInput: $createAttributeInput) {
            ...SampleAttribute
          }
        }
      }
      ${Fragments.sampleAttribute}
    `,
    variables: {
      createAttributeInput: payload,
    },
    rootField: "sample.createAttribute",
  };
}

export function getModifyAttributeQueryOptions(payload: InputAttributeInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifyAttribute($attributeInput: InputAttributeInput) {
        sample {
          modifyAttribute(attributeInput: $attributeInput) {
            ...SampleAttribute
          }
        }
      }
      ${Fragments.sampleAttribute}
    `,
    variables: {
      attributeInput: payload,
    },
    rootField: "sample.modifyAttribute",
  };
}

export function getCreateUsersQueryOptions(users: InputUserAccountInput[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createUsers($newUsers: [InputUserAccountInput]) {
        admin {
          createUsers(newUsers: $newUsers) {
            ...UserForAdmin
          }
        }
      }
      ${Fragments.userForAdmin}
    `,
    variables: {
      newUsers: users,
    },
    rootField: "admin.createUsers",
  };
}

export function getModifyUserQueryOptions(userAccountInput: InputUserAccountInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifyUser($user: InputUserAccountInput) {
        admin {
          modifyUser(user: $user) {
            ...UserForAdmin
          }
        }
      }
      ${Fragments.userForAdmin}
    `,
    variables: {
      user: userAccountInput,
    },
    rootField: "admin.modifyUser",
  };
}

export function getSaveSampleTypeQueryOptions(newSampleType: InputSampleTypeInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createSampleType($createAttributeInput: InputSampleTypeInput) {
        sample {
          createSampleType(createSampleTypeInput: $createAttributeInput) {
            ...SampleType
          }
        }
      }
      ${Fragments.sampleType}
    `,
    variables: {
      createAttributeInput: newSampleType,
    },
    rootField: "sample.createSampleType",
  };
}

export function getModifySampleTypeQueryOptions(sampleType: InputSampleTypeInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifySampleType($editSampleTypeInput: InputSampleTypeInput) {
        sample {
          editSampleType(editSampleTypeInput: $editSampleTypeInput) {
            ...SampleType
          }
        }
      }
      ${Fragments.sampleType}
    `,
    variables: {
      editSampleTypeInput: sampleType,
    },
    rootField: "sample.editSampleType",
  };
}

export function getSaveParentTypeQueryOptions(newParentType: InputParentTypeInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createParentType($createParentTypeInput: InputParentTypeInput) {
        sample {
          createParentType(createParentTypeInput: $createParentTypeInput) {
            ...ParentType
          }
        }
      }
      ${Fragments.parentType}
    `,
    variables: {
      createParentTypeInput: newParentType,
    },
    rootField: "sample.createParentType",
  };
}

export function getModifyParentTypeQueryOptions(parentType: InputParentTypeInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifyParentType($editParentTypeInput: InputParentTypeInput) {
        sample {
          editParentType(editParentTypeInput: $editParentTypeInput) {
            ...ParentType
          }
        }
      }
      ${Fragments.parentType}
    `,
    variables: {
      editParentTypeInput: parentType,
    },
    rootField: "sample.editParentType",
  };
}

export function getDeleteAttributesQueryOptions(attributeIds: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteAttributes($attributeIds: [Long]) {
        sample {
          deleteAttributes(attributeIds: $attributeIds)
        }
      }
    `,
    variables: {
      attributeIds: attributeIds,
    },
    rootField: "sample.deleteAttributes",
  };
}

export function getDeleteSampleTypesQueryOptions(ids: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteSampleTypes($sampleTypeIds: [Long]!) {
        sample {
          deleteSampleTypes(sampleTypeIds: $sampleTypeIds)
        }
      }
    `,
    variables: {
      sampleTypeIds: ids,
    },
    rootField: "deleteSampleTypes",
  };
}

export function getSetSampleLockingQueryOptions(sampleIds: number[], lockingAction: string): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifySampleLockingStatus($sampleIds: [Long!]!, $locked: Boolean!) {
        sample {
          modifySampleLockingStatus(sampleIds: $sampleIds, locked: $locked)
        }
      }
    `,
    variables: {
      sampleIds: sampleIds,
      locked: lockingAction === SAMPLE_LOCKING_ACTIONS.LOCK,
    },
    rootField: "modifySampleLockingStatus",
  };
}

export function getDeleteParentTypesQueryOptions(ids: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteParentTypes($parentTypeIds: [Long]!) {
        sample {
          deleteParentTypes(parentTypeIds: $parentTypeIds)
        }
      }
    `,
    variables: {
      parentTypeIds: ids,
    },
    rootField: "deleteParentTypes",
  };
}

export function getDeleteParentsQueryOptions(ids: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteParents($parentIds: [Long]!) {
        sample {
          deleteParents(parentIds: $parentIds)
        }
      }
    `,
    variables: {
      parentIds: ids,
    },
    rootField: "deleteParents",
  };
}

/* NCBIAgent stuff */
export function getInstallNcbiGenomes(downloadOptions: InputGenomeAssembliesInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation installNCBIGenomes($inputData: InputGenomeAssembliesInput) {
        admin {
          tools {
            installNCBIGenomes(inputData: $inputData)
          }
        }
      }
    `,
    variables: {
      inputData: downloadOptions,
    },
    rootField: "admin.tools.installNCBIGenomes",
  };
}

export function getUpdateNcbiGenomeListOptions(withStats: boolean): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation updateNCBIGenomes($arg0: Boolean) {
        admin {
          tools {
            updateNCBIGenomes(arg0: $arg0)
          }
        }
      }
    `,
    variables: {
      arg0: withStats,
    },
    rootField: "admin.tools.updateNCBIGenomes",
  };
}

export function getSaveNewPlaybookQueryOptions(playbookPayload: InputPlaybookInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createPlaybook($playbookInput: InputPlaybookInput!) {
        admin {
          createPlaybook(playbookInput: $playbookInput) {
            id
          }
        }
      }
    `,
    variables: {
      playbookInput: playbookPayload,
    },
    rootField: "admin.createPlaybook",
  };
}

export function getSaveNewPlaybookCategory(playbookCategoryInput: InputPlaybookCategoryInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation createPlaybookCategory($playbookCategoryInput: InputPlaybookCategoryInput!) {
        admin {
          createPlaybookCategory(playbookCategoryInput: $playbookCategoryInput) {
            ...PlaybookCategoryBasics
          }
        }
      }
      ${Fragments.playbookCategoryBasics}
    `,
    variables: {
      playbookCategoryInput: playbookCategoryInput,
    },
    rootField: "admin.createPlaybookCategory",
  };
}

export function getModifyPlaybookCategory(playbookCategoryInput: InputPlaybookCategoryInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifyPlaybookCategory($playbookCategoryInput: InputPlaybookCategoryInput!) {
        admin {
          modifyPlaybookCategory(playbookCategoryInput: $playbookCategoryInput) {
            ...PlaybookCategoryBasics
          }
        }
      }
      ${Fragments.playbookCategoryBasics}
    `,
    variables: {
      playbookCategoryInput: playbookCategoryInput,
    },
    rootField: "admin.modifyPlaybookCategory",
  };
}

export function getModifyPlaybookEvent(
  modifyPlaybookEventInput: InputModifyPlaybookEventInput,
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation editPlaybookEvent($modifyPlaybookEventInput: InputModifyPlaybookEventInput!) {
        playbook {
          editPlaybookEvent(modifyPlaybookEventInput: $modifyPlaybookEventInput) {
            ...EventSummary
          }
        }
      }
      ${Fragments.eventSummary}
    `,
    variables: {
      modifyPlaybookEventInput: modifyPlaybookEventInput,
    },
    rootField: "playbook.editPlaybookEvent",
  };
}

export function getDeletePlaybookCategory(playbookCategoryIds: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deletePlaybookCategories($playbookCategoryIds: [Long]!) {
        admin {
          deletePlaybookCategories(playbookCategoryIds: $playbookCategoryIds) {
            ...PlaybookCategoryBasics
          }
        }
      }
      ${Fragments.playbookCategoryBasics}
    `,
    variables: {
      playbookCategoryIds: playbookCategoryIds,
    },
    rootField: "admin.deletePlaybookCategories",
  };
}

export function getModifyPlaybookQueryOptions(playbookPayload: InputPlaybookInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifyPlaybook($playbookInput: InputPlaybookInput!) {
        admin {
          modifyPlaybook(playbookInput: $playbookInput) {
            id
          }
        }
      }
    `,
    variables: {
      playbookInput: playbookPayload,
    },
    rootField: "admin.modifyPlaybook",
  };
}

export function getSaveDashboardMutationOptions(dashboard: InputDashboardInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation saveDashboard($dashboardInput: InputDashboardInput!) {
        updateDashboard(dashboardInput: $dashboardInput)
      }
    `,
    variables: { dashboardInput: dashboard },
    rootField: "updateDashboard",
  };
}

export function getInvalidatePasswordsQueryOptions(usernames: string[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation invalidatePasswords($users: [String]!) {
        admin {
          forcePasswordChange(userNames: $users)
        }
      }
    `,
    variables: { users: usernames },
    rootField: "admin.forcePasswordChange",
  };
}

export function getChangeYourPasswordQueryOptions(
  payload: InputUserPasswordChangeRequestInput,
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation changeYourPassword($userPasswordChangeRequestInput: InputUserPasswordChangeRequestInput) {
        user {
          changeYourPassword(userPasswordChangeRequestInput: $userPasswordChangeRequestInput)
        }
      }
    `,
    variables: { userPasswordChangeRequestInput: payload },
    rootField: "user.changeYourPassword",
  };
}

export function generateRestToken(lifetime: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation generateRestToken($lifetime: Int) {
        user {
          generateRestToken(lifetime: $lifetime)
        }
      }
    `,
    variables: { lifetime: lifetime },
    rootField: "user.generateRestToken",
  };
}

export function getModifyProjectFileOptions(fileInput: InputModifyProjectFileInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation modifyProjectFile($modifyProjectFileInput: InputModifyProjectFileInput) {
        modifyProjectFile(modifyProjectFileInput: $modifyProjectFileInput) {
          id
        }
      }
    `,
    variables: { modifyProjectFileInput: fileInput },
    rootField: "modifyProjectFile",
  };
}

export function getIndexBamFileOptions(fileId: number): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation indexBamFile($fileId: Long) {
        indexBamFile(fileId: $fileId)
      }
    `,
    variables: { fileId: fileId },
    rootField: "indexBamFile",
  };
}

export function getSaveOrgPropertyTypeQueryOptions(payload: InputOrganismPropertyTypeInput): ICustomMutationOptions {
  if (payload.id) {
    return {
      mutation: gql`
        mutation modifyOrgPropertyType($organismPropertyTypeInput: InputOrganismPropertyTypeInput!) {
          modifyOrganismPropertyType(organismPropertyTypeInput: $organismPropertyTypeInput) {
            id
          }
        }
      `,
      variables: { organismPropertyTypeInput: payload },
      rootField: "modifyOrganismPropertyType",
    };
  } else {
    return {
      mutation: gql`
        mutation createOrgPropertyType($organismPropertyTypeInput: InputOrganismPropertyTypeInput!) {
          createOrganismPropertyType(organismPropertyTypeInput: $organismPropertyTypeInput) {
            id
          }
        }
      `,
      variables: { organismPropertyTypeInput: payload },
      rootField: "createOrganismPropertyType",
    };
  }
}

export function getDeleteOrgPropertyTypesQueryOptions(orgPropertyTypeIds: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteOrganismPropertyTypes($orgPropertyTypeIds: [Long]!) {
        deleteOrganismPropertyTypes(orgPropertyTypeIds: $orgPropertyTypeIds)
      }
    `,
    variables: { orgPropertyTypeIds: orgPropertyTypeIds },
    rootField: "deleteOrganismPropertyTypes",
  };
}

export function getEditOrganismQueryOptions(changes: InputModifyOrganismInput[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation editOrganism($modifyOrganismInputDataList: [InputModifyOrganismInput]!) {
        editOrganism(modifyOrganismInputDataList: $modifyOrganismInputDataList)
      }
    `,
    variables: { modifyOrganismInputDataList: changes },
    rootField: "editOrganism",
  };
}

export function getSaveParentQueryOptions(editParentInput: InputEditParentInput): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation editParent($editParentInput: InputEditParentInput!) {
        sample {
          editParentAttributes(editParentInput: $editParentInput) {
            ...Parent
          }
        }
      }
      ${Fragments.parent}
    `,
    variables: { editParentInput: editParentInput },
    rootField: "sample.editParentAttributes",
  };
}

export function getSubmitNextflowPlaybookOptions(
  playbookEventInput: InputPlaybookEventInput,
  workflowParams: InputNfParamInput[],
  sampleEditingInput: InputEditSamplesInput,
): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation submitPlaybook(
        $playbookEventInput: InputPlaybookEventInput
        $workflowParams: [InputNFParamInput]
        $editSamplesInput: InputEditSamplesInput
      ) {
        playbook {
          submit(
            playbookEventInput: $playbookEventInput
            workflowParams: $workflowParams
            editSamplesInput: $editSamplesInput
          )
        }
      }
    `,
    variables: {
      playbookEventInput: playbookEventInput,
      workflowParams: workflowParams,
      editSamplesInput: sampleEditingInput,
    },
    rootField: "playbook.submit",
  };
}

export function getDeleteUploadedSequencesQueryOptions(sequenceIds: number[]): ICustomMutationOptions {
  return {
    mutation: gql`
      mutation deleteUploadedSequences($sequenceIds: [Long]!) {
        deleteUploadSequences(sequenceIds: $sequenceIds)
      }
    `,
    variables: {
      sequenceIds: sequenceIds,
    },
    rootField: "deleteUploadSequences",
  };
}
